import i18next from 'i18next';
import { CboRole, FeatureFlag } from '@cbo/shared-library';
import isPermitted from '../../../lib/permissions';
import { useUsers } from '../../../contexts/userContext';
import routes from '../../../constants/routes';
import { NavItem } from '.';
import { useFeatureFlags } from '../../../utils/hooks/useFeatureFlag';
import useCountingAndLocationRoutes from './useCountingAndLocationRoutes';

export default function useInventoryRoutes(): NavItem[] {
  const user = useUsers();
  const [mobileInventoryEnabled, corePackageInventory, recipeEnabled, cogs] = useFeatureFlags(
    FeatureFlag.mobileInventory,
    FeatureFlag.corePackageInventory,
    FeatureFlag.recipe,
    FeatureFlag.cogs
  );
  const inventoryRoutes: NavItem[] = [];
  const countingAndLocationsRoutes = useCountingAndLocationRoutes();

  if (isPermitted(user, [CboRole.VENDOR_VIEW])) {
    inventoryRoutes.push({
      title: i18next.t('navItems.vendors'),
      href: routes.VENDORS,
      testid: 'vendors',
    });
  }

  if (isPermitted(user, [CboRole.RAW_ITEM_VIEW])) {
    inventoryRoutes.push({
      title: i18next.t('navItems.rawItems'),
      href: routes.RAW_ITEMS,
      testid: 'raw-items',
    });
  }

  if (corePackageInventory && isPermitted(user, [CboRole.UNITS_OF_MEASURE_VIEW])) {
    inventoryRoutes.push({
      title: i18next.t('navItems.unitsOfMeasure'),
      href: routes.UNITS_OF_MEASURE,
      testid: 'units-of-measure',
    });
  }

  if (isPermitted(user, [CboRole.INVOICE_VIEW])) {
    inventoryRoutes.push({
      title: i18next.t('navItems.invoices'),
      href: routes.INVOICES,
      testid: 'invoice-items',
    });
  }

  if (isPermitted(user, [CboRole.INVOICE_VIEW])) {
    inventoryRoutes.push({
      title: i18next.t('navItems.invoiceHistory'),
      href: routes.INVOICE_HISTORY_REPORT,
      testid: 'invoice-history-report',
    });
  }

  if (corePackageInventory) {
    const menu = [];

    if (mobileInventoryEnabled && isPermitted(user, [CboRole.MOBILE_INVENTORY_VIEW])) {
      inventoryRoutes.push({
        title: i18next.t('navItems.countingAndLocations'),
        href: routes.COUNTING_AND_LOCATIONS,
        testid: 'counting-and-locations',
        forceNewFrame: true,
        backHref: '/inventory',
        children: countingAndLocationsRoutes,
      });
    }

    if (isPermitted(user, [CboRole.SALES_ITEM_VIEW])) {
      menu.push({
        title: i18next.t('navItems.salesItems'),
        href: routes.SALES_ITEMS,
        testid: 'sales-items',
      });
      menu.push({
        title: i18next.t('navItems.modifierGroups'),
        href: routes.MODIFIER_GROUPS,
        testid: 'modifier-groups',
      });

      if (recipeEnabled && isPermitted(user, [CboRole.RECIPE_VIEWER])) {
        menu.push({
          title: i18next.t('navItems.recipes'),
          href: routes.RECIPES,
          testid: 'recipes',
        });
      }

      inventoryRoutes.push({
        title: i18next.t('navItems.menu'),
        href: routes.MENU,
        testid: 'menu',
        forceNewFrame: true,
        backHref: '/inventory',
        children: menu,
      });
    }

    const inventoryReportRoutes: NavItem[] = [];
    if (cogs && isPermitted(user, [CboRole.COGS_REPORT_VIEW])) {
      inventoryReportRoutes.push({
        title: i18next.t('navItems.cogs'),
        href: routes.COGS_REPORT,
        testid: 'cogs-report',
      });
    }

    if (inventoryReportRoutes.length > 0) {
      inventoryRoutes.push({
        title: i18next.t('navItems.reports'),
        testid: 'inventory-reports',
        children: inventoryReportRoutes,
      });
    }
  }

  return inventoryRoutes;
}
